import { createFileRoute, Link } from "@tanstack/react-router";
import { GROUPS, FAQ_PAGE_REDIRECTS, faqSlug, type Faq } from "@/lib/faq-data";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FinalCta } from "@/components/site/FinalCta";
import harrisSq from "@/assets/restore/dr-harris-sq.jpg.asset.json";
import { renderToStaticMarkup } from "react-dom/server";
import { htmlToText } from "@/lib/meta-text";
import { safeJsonLd } from "@/lib/structured-data";

export const Route = createFileRoute("/hair-transplant-faqs")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/hair-transplant-faqs/" }],
    meta: [
      { title: "Hair Transplant FAQs & Hair Transplant Questions" },
      {
        name: "description",
        content:
          "Learn about male pattern baldness & hair loss via our Hair Transplant FAQs to see if you're in need of getting your hair restored. Ask our client advisors!",
      },
      { property: "og:title", content: "Hair Transplant FAQs — RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Everything men ask us about hair transplants — from cost and pain to recovery, results, and non-surgical options.",
      },
    ],
    // The WordPress page carried FAQPage markup for every question. Same here.
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: GROUPS.flatMap((g) => g.items).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: htmlToText(renderToStaticMarkup(<>{f.a}</>)) },
          })),
        }),
      },
    ],
  }),
  component: HairTransplantFaqsPage,
});


function FaqRow({ q, a }: Faq) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border bg-background">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-muted/40"
      >
        <span className="text-base font-bold text-foreground md:text-lg">{q}</span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-primary transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {/* Always in the HTML, hidden until opened. Crawlers then see the answer
          and the link to the FAQ's own page. When the answer rendered only
          after a click, the 80 /faq/ pages had no crawlable link from here. */}
      <div hidden={!open}>
        <div className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground md:text-base">
          {a}
          {FAQ_PAGE_REDIRECTS[faqSlug(q)] ? (
            // This question's own page now redirects to a longer article.
            <a
              href={FAQ_PAGE_REDIRECTS[faqSlug(q)]}
              className="mt-3 inline-block text-sm font-semibold text-primary underline underline-offset-2 hover:opacity-80"
            >
              Read the full guide
            </a>
          ) : (
            <Link
              to="/faq/$slug/"
              params={{ slug: faqSlug(q) }}
              className="mt-3 inline-block text-sm font-semibold text-primary underline underline-offset-2 hover:opacity-80"
            >
              Read this answer on its own page
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

const JUMP_LABELS = ["Consultation", "Pre-Op", "Surgery Day", "Post-Op", "Non-Surgical", "Other"];

const slugFor = (i: number) =>
  ["consultation", "pre-op", "surgery-day", "post-op", "non-surgical", "other"][i] ?? `faq-${i}`;

function HairTransplantFaqsPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-[#e9e9e9] py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <div className="flex flex-col items-center gap-6 bg-background px-6 py-8 text-center md:flex-row md:justify-between md:px-10 md:text-left">
              <div className="flex flex-col items-center gap-5 md:flex-row">
                <img
                  src={harrisSq.url}
                  alt="Dr. James Harris, RESTORE Hair"
                  className="h-28 w-28 flex-shrink-0 rounded-full object-cover"
                  loading="eager"
                />
                <p className="max-w-xs text-lg leading-snug text-muted-foreground">
                  <span className="font-bold text-foreground">Next step:</span> Let's schedule your
                  complimentary consultation
                </p>
              </div>
              <a
                href="/schedule-a-consult/"
                className="bg-primary px-8 py-4 text-base font-bold text-primary-foreground transition hover:opacity-90"
              >
                Yes, Book Now!
              </a>
            </div>
          </div>
        </section>

        <section className="bg-muted/40 pt-12 md:pt-16">
          <div className="mx-auto max-w-6xl px-4 text-center lg:px-8">
            <h1 className="text-3xl font-extrabold leading-tight text-foreground md:text-5xl">
              Hair Transplant FAQs
            </h1>
            <p className="mt-2 text-2xl text-foreground md:text-3xl">Frequently Asked Questions</p>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {JUMP_LABELS.map((label, i) => (
                <a
                  key={label}
                  href={`#${slugFor(i)}`}
                  className="flex items-center justify-center bg-primary px-3 py-8 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-muted/40 py-10 md:py-14">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            {GROUPS.map((g, i) => (
              <div key={g.title} id={slugFor(i)} className="mb-12 scroll-mt-28 last:mb-0">
                <h2 className="mb-6 text-center text-2xl font-normal text-foreground md:text-4xl">
                  {g.title}
                </h2>
                <div className="space-y-3">
                  {g.items.map((f) => (
                    <FaqRow key={f.q} q={f.q} a={f.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <FinalCta variant="light" />
      </main>
      <Footer />
    </div>
  );
}